import { FC, useState, useMemo } from "react"
import {
  Users,
  DollarSign,
  Wallet,
  TrendingUp,
  Calendar,
  BarChart3
} from "lucide-react"
import { Channel, ChannelStatsItem } from "../../../../types"
import { useGetChannelStatsQuery } from "../../../../redux/queries"
import If from "../../if"
import Table from "../../table"
import { BBColumnDef } from "../../../../types"

interface Props {
  orgId: string
  channels: Channel[]
}

// Helper to get date range presets
const getDateRange = (preset: string) => {
  const now = new Date()
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  )

  switch (preset) {
    case "today":
      return {
        start: Math.floor(startOfToday.getTime() / 1000),
        stop: Math.floor(now.getTime() / 1000)
      }
    case "week":
      const weekAgo = new Date(startOfToday)
      weekAgo.setDate(weekAgo.getDate() - 7)
      return {
        start: Math.floor(weekAgo.getTime() / 1000),
        stop: Math.floor(now.getTime() / 1000)
      }
    case "month":
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
      return {
        start: Math.floor(monthStart.getTime() / 1000),
        stop: Math.floor(now.getTime() / 1000)
      }
    case "quarter":
      const quarterStart = new Date(
        now.getFullYear(),
        Math.floor(now.getMonth() / 3) * 3,
        1
      )
      return {
        start: Math.floor(quarterStart.getTime() / 1000),
        stop: Math.floor(now.getTime() / 1000)
      }
    default:
      return { start: undefined, stop: undefined }
  }
}

const StatsTab: FC<Props> = ({ orgId }) => {
  const [dateRange, setDateRange] = useState("month")

  const { start, stop } = useMemo(() => getDateRange(dateRange), [dateRange])

  const { data: stats, isFetching } = useGetChannelStatsQuery(
    { org_id: orgId, start, stop },
    { skip: !orgId }
  )

  // Transform channel stats into array for table
  const channelStatsArray = useMemo(() => {
    if (!stats?.channels) return []
    return Object.values(stats.channels) as ChannelStatsItem[]
  }, [stats?.channels])

  const columns: BBColumnDef<ChannelStatsItem>[] = [
    {
      id: "name",
      header: "Channel",
      accessorFn: row => row.channel.name,
      cell: props => (
        <td className="py-4 px-4 align-middle min-w-[160px]">
          <p className="font-semibold text-gray-900">
            {props.row.original.channel.name}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            ${props.row.original.channel.cpl_dollars}/lead
          </p>
        </td>
      )
    },
    {
      id: "leads",
      header: "Leads",
      accessorFn: row => row.stats.lead_count,
      cell: props => (
        <td className="py-4 px-4 align-middle min-w-[80px]">
          <span className="text-lg font-bold text-gray-900 tabular-nums">
            {props.row.original.stats.lead_count.toLocaleString()}
          </span>
        </td>
      )
    },
    {
      id: "spend",
      header: "Total Spend",
      accessorFn: row => row.stats.total_charged_cents,
      cell: props => (
        <td className="py-4 px-4 align-middle min-w-[110px]">
          <span className="text-lg font-bold text-gray-900 tabular-nums">
            ${props.row.original.stats.total_charged_dollars}
          </span>
        </td>
      )
    },
    {
      id: "avg_cpl",
      header: "Avg CPL",
      accessorFn: row => row.stats.avg_cpl_cents,
      cell: props => (
        <td className="py-4 px-4 align-middle min-w-[90px]">
          <span className="font-semibold text-gray-700 tabular-nums">
            ${props.row.original.stats.avg_cpl_dollars}
          </span>
        </td>
      )
    },
    {
      id: "status",
      header: "Status",
      accessorFn: row => row.channel.status,
      cell: props => {
        const status = props.row.original.channel.status
        const statusConfig: Record<
          string,
          { bg: string; text: string; dot: string }
        > = {
          active: {
            bg: "bg-emerald-50",
            text: "text-emerald-700",
            dot: "bg-emerald-500"
          },
          paused: {
            bg: "bg-amber-50",
            text: "text-amber-700",
            dot: "bg-amber-500"
          },
          disabled: {
            bg: "bg-gray-100",
            text: "text-gray-600",
            dot: "bg-gray-400"
          }
        }
        const config = statusConfig[status] || statusConfig.disabled
        return (
          <td className="py-4 px-4 align-middle">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold capitalize ${config.bg} ${config.text}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
              {status}
            </span>
          </td>
        )
      }
    }
  ]

  const dateRangeOptions = [
    { id: "today", label: "Today" },
    { id: "week", label: "Last 7 days" },
    { id: "month", label: "This month" },
    { id: "quarter", label: "This quarter" }
  ]

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-gray-400" />
          <span className="text-sm text-gray-500">Period:</span>
        </div>
        <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
          {dateRangeOptions.map(option => (
            <button
              key={option.id}
              onClick={() => setDateRange(option.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                dateRange === option.id
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Leads */}
        <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-blue-100 rounded-xl">
                <Users size={20} className="text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">
                Total Leads
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900 tabular-nums">
              {isFetching ? (
                <span className="animate-pulse">—</span>
              ) : (
                stats?.totals?.total_leads?.toLocaleString() ?? "0"
              )}
            </p>
          </div>
        </div>

        {/* Total Spend */}
        <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <div className="absolute top-0 right-0 w-24 h-24 bg-rose-50 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-rose-100 rounded-xl">
                <DollarSign size={20} className="text-rose-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">
                Total Spend
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900 tabular-nums">
              {isFetching ? (
                <span className="animate-pulse">—</span>
              ) : (
                `$${stats?.totals?.total_spend_dollars ?? "0.00"}`
              )}
            </p>
          </div>
        </div>

        {/* Current Balance */}
        <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-50 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-emerald-100 rounded-xl">
                <Wallet size={20} className="text-emerald-600" />
              </div>
              <span className="text-sm font-medium text-gray-500">
                Current Balance
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900 tabular-nums">
              {isFetching ? (
                <span className="animate-pulse">—</span>
              ) : (
                `$${stats?.totals?.current_balance_dollars ?? "0.00"}`
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Period Info */}
      {stats?.period && (
        <div className="text-sm text-gray-500 text-center">
          Showing data from {stats.period.start_formatted} to{" "}
          {stats.period.stop_formatted}
        </div>
      )}

      {/* Per-Channel Breakdown */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-xl">
              <BarChart3 size={18} className="text-gray-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Per-Channel Breakdown
              </h3>
              <p className="text-sm text-gray-500">
                Performance by channel source
              </p>
            </div>
          </div>
        </div>

        <If
          condition={isFetching}
          then={
            <div className="flex items-center justify-center py-12">
              <div className="animate-pulse text-gray-400">
                Loading stats...
              </div>
            </div>
          }
          else={
            <If
              condition={!channelStatsArray.length}
              then={
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <TrendingUp size={48} className="mb-3 opacity-50" />
                  <p className="font-medium">No channel data yet</p>
                  <p className="text-sm">
                    Stats will appear once leads are received
                  </p>
                </div>
              }
              else={
                <Table
                  data={channelStatsArray}
                  columns={columns}
                  viewType="table"
                  shrinkFilterSort
                  searchBarVisible={false}
                  defaultSortBy="leads"
                  defaultSortDir="desc"
                  tableClassName="table-auto"
                  tableHeaderClassName="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
                  dividedRows={false}
                />
              }
            />
          }
        />
      </div>
    </div>
  )
}

export default StatsTab
